import React, { useState } from "react";
import { motion, useInView } from "framer-motion";
import { ChevronDown, Quote } from "lucide-react";

const M = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const Fade = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const FounderNoteSection = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <section
      id="founder"
      data-testid="founder-note-section"
      className="bg-[#0D1F17] relative overflow-hidden"
    >
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #D4AF37 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left: Large zoomed portrait */}
        <div className="relative h-[480px] sm:h-[560px] lg:h-auto lg:min-h-[650px] overflow-hidden" data-testid="founder-image-container">
          <img
            src={`${M}/nadiya-founder.webp`}
            alt="Nadiya — Founder of Fitly India"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 5%", transform: "scale(2.2)", transformOrigin: "center 12%" }}
            data-testid="founder-image"
          />
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1F17] via-[#0D1F17]/20 to-transparent opacity-70 lg:hidden" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0D1F17]/80 opacity-0 lg:opacity-100" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1F17]/90 via-transparent to-transparent opacity-0 lg:opacity-60" />

          {/* Name overlay on mobile */}
          <div className="absolute bottom-0 left-0 right-0 p-6 lg:hidden">
            <p className="font-serif text-3xl text-[#FEFDFB] italic tracking-tight">Nadiya</p>
            <div className="h-px bg-[#D4AF37]/40 w-16 mt-2 mb-2" />
            <p className="text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-bold">
              Founder, Fitly India
            </p>
          </div>

          {/* Gold accent line on left edge — desktop */}
          <div className="absolute top-16 left-0 w-px h-24 bg-gradient-to-b from-transparent via-[#D4AF37]/50 to-transparent hidden lg:block" />
        </div>

        {/* Right: Founder's note — editorial style */}
        <div className="relative px-8 py-12 sm:px-12 sm:py-16 lg:px-16 lg:py-20 flex flex-col justify-center">
          {/* Ambient glow */}
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[300px] h-[300px] bg-[#D4AF37]/[0.03] rounded-full blur-3xl pointer-events-none" />

          <Fade>
            {/* Badge */}
            <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#D4AF37]">
              From the Founder
            </span>

            {/* Name — desktop only (mobile shows on photo) */}
            <div className="hidden lg:block mt-4 mb-6">
              <h2 className="font-serif text-4xl md:text-5xl text-[#FEFDFB] tracking-tight leading-none">
                Nadiya
              </h2>
              <div className="h-px bg-[#D4AF37]/30 w-20 mt-3 mb-2" />
              <p className="text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-bold">
                Founder, Fitly India
              </p>
            </div>

            {/* Mobile heading */}
            <h2 className="lg:hidden font-serif text-2xl text-[#FEFDFB] tracking-tight mt-3 mb-6">
              A Note From <span className="italic font-light text-[#D4AF37]">Nadiya</span>
            </h2>
          </Fade>

          <Fade delay={0.15}>
            <div data-testid="founder-story" className="relative">
              {/* Large decorative quote mark */}
              <Quote
                size={48}
                className="text-[#D4AF37]/15 absolute -top-4 -left-3 rotate-180"
              />

              <p className="font-serif italic text-[#FEFDFB]/90 text-xl md:text-2xl leading-relaxed tracking-tight pl-6 border-l-2 border-[#D4AF37]/30">
                I founded Fitly in 2020 with a vision to make nutrition more personalised, sustainable and compassionate.
              </p>

              <p className="text-[#FEFDFB]/50 text-sm leading-relaxed mt-6" style={{ fontWeight: 450 }}>
                What began as a weight-management practice evolved into a specialised{" "}
                <span className="font-semibold text-[#FEFDFB]/80">Conception Care Team</span>,
                shaped by the outcomes we witnessed and the needs of the couples we supported.
              </p>

              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.4 }}
                  className="mt-5 space-y-4 text-[#FEFDFB]/50 text-sm leading-relaxed overflow-hidden"
                  style={{ fontWeight: 450 }}
                >
                  <p>
                    Before Fitly, I spent nearly nine years in the corporate world, working with{" "}
                    <span className="font-semibold text-[#FEFDFB]/80">LinkedIn</span> and{" "}
                    <span className="font-semibold text-[#FEFDFB]/80">Oracle</span>.
                    During my pregnancy in the COVID period, health became an even greater priority, leading me to complete
                    Precision Nutrition Level 1 & 2, become a Deep Health Certified Coach and a Certified Pre-Conception Counsellor.
                  </p>
                  <p>
                    Today, Fitly is a multidisciplinary conception care ecosystem — clinical nutritionists,
                    gynaecologists, andrologist, psychiatrist, Ayurvedic fertility specialist, yoga professionals,
                    strength coaches, and childbirth educators — all working together.
                  </p>
                  <div className="py-5 px-6 bg-[#FEFDFB]/[0.03] rounded-xl border-l-2 border-[#D4AF37]/40 mt-2">
                    <p className="font-serif text-base text-[#FEFDFB]/80 italic leading-relaxed tracking-tight">
                      &quot;Fitly is not about one individual. It is a specialised team working together to help couples
                      build healthier foundations for conception and beyond.&quot;
                    </p>
                  </div>
                  <div data-testid="founder-signature" className="pt-3">
                    <div className="font-serif text-2xl text-[#FEFDFB] italic tracking-wide leading-none">Nadiya</div>
                    <div className="h-px bg-[#D4AF37]/30 w-20 mt-2 mb-2" />
                    <p className="text-[9px] text-[#D4AF37] tracking-[0.2em] uppercase font-bold">
                      Founder of Fitly India
                    </p>
                  </div>
                </motion.div>
              )}

              <button
                onClick={() => setExpanded(!expanded)}
                data-testid="founder-read-more"
                className="mt-5 inline-flex items-center gap-1.5 text-[#D4AF37] text-sm font-medium hover:text-[#E8D48B] transition-colors group"
              >
                {expanded ? "Show Less" : "Read Her Story"}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${expanded ? "rotate-180" : "group-hover:translate-y-0.5"}`}
                />
              </button>
            </div>
          </Fade>

          {/* Credentials */}
          <Fade delay={0.25}>
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#FEFDFB]/[0.06]">
              {["Precision Nutrition L2", "Deep Health Coach", "Pre-Conception Counsellor"].map((c) => (
                <span
                  key={c}
                  className="text-[9px] tracking-[0.12em] uppercase bg-[#D4AF37]/10 text-[#D4AF37]/80 px-4 py-1.5 rounded-full font-semibold border border-[#D4AF37]/15"
                >
                  {c}
                </span>
              ))}
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
};

export default FounderNoteSection;
